// Conectores: el POS, la contabilidad, la tienda en línea, un sistema interno o el propio Glubi, conectados una
// vez y expuestos a los agentes del negocio por un solo MCP. Un servidor MCP se descubre solo; una API REST se
// describe operación por operación y cada una se vuelve una herramienta.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Cargando, Input, Modal, PageHead, Select, Tag, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { desde, numero } from '../lib/formato';
import { conectoresPlantilla, type OperacionPlantilla, type PlantillaConector } from '../lib/plantillas';
import { useSesion } from '../lib/sesion';
import { Area, AvisoSoloVer, Copiable, Estado, SoloSiAdministra } from '../componentes/Comunes';
import { credencialInicial, ElegirCredencial, resolverCredencial, type EstadoCredencial } from '../componentes/ElegirCredencial';

type Auth = PlantillaConector['autenticacion'];
interface Operacion extends Omit<OperacionPlantilla, 'parametros'> { parametros: string }
interface Form {
  id: string | null; nombre: string; titulo: string; descripcion: string; tipo: 'mcp' | 'http'; url: string; autenticacion: Auth; campo: string;
  credencial: EstadoCredencial; operaciones: Operacion[]; ocultas: string[]; estado: 'activo' | 'pausado'; ayuda?: string;
}

const AUTH: { value: Auth; label: string }[] = [
  { value: 'bearer', label: 'Bearer (Authorization: Bearer …)' },
  { value: 'encabezado', label: 'Encabezado propio (x-api-key, …)' },
  { value: 'basica', label: 'Básica (usuario:contraseña)' },
  { value: 'consulta', label: 'Parámetro en la URL (?api_key=…)' },
  { value: 'ninguna', label: 'Sin autenticación' },
];

const aTexto = (p?: Record<string, unknown>) => (p ? JSON.stringify(p, null, 2) : '');
const slug = (t: string) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30);

function desdePlantilla(p: PlantillaConector): Form {
  return {
    id: null, nombre: p.nombre, titulo: p.clave === 'mcp' || p.clave === 'http' ? '' : p.titulo, descripcion: p.clave === 'mcp' || p.clave === 'http' ? '' : p.descripcion,
    tipo: p.tipo, url: p.url, autenticacion: p.autenticacion, campo: p.campo ?? '', ocultas: [], estado: 'activo', ayuda: p.ayudaCredencial,
    credencial: p.autenticacion === 'ninguna' ? { modo: 'ninguna' } : { modo: 'nueva', nombre: `Credencial de ${p.titulo}`, valor: '' },
    operaciones: (p.operaciones ?? []).map((o) => ({ ...o, parametros: aTexto(o.parametros) })),
  };
}

export function Conectores() {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['conectores'], queryFn: () => api<any[]>('/api/conectores') });
  const config = useQuery({ queryKey: ['config'], queryFn: () => api<{ mcp: string; mcpGlubi: string }>('/api/config'), staleTime: Infinity });
  const [elegir, setElegir] = useState(false);
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [prueba, setPrueba] = useState<{ conector: any; herramienta: any; args: string; resultado?: string; error?: boolean } | null>(null);

  const recargar = () => { void qc.invalidateQueries({ queryKey: ['conectores'] }); void qc.invalidateQueries({ queryKey: ['secretos'] }); };

  const guardar = useMutation({
    mutationFn: async (f: Form) => {
      const operaciones = f.tipo === 'http' ? f.operaciones.map((o) => {
        let parametros: Record<string, unknown> | undefined;
        if (o.parametros.trim()) {
          try { parametros = JSON.parse(o.parametros); } catch { throw new Error(`Los parámetros de «${o.nombre}» no son JSON válido`); }
        }
        return { nombre: o.nombre, descripcion: o.descripcion, metodo: o.metodo, ruta: o.ruta, ...(parametros ? { parametros } : {}) };
      }) : [];
      const secretoId = f.autenticacion === 'ninguna' ? null : await resolverCredencial(f.credencial);
      return api<{ id: string; herramientas: number; error: string | null }>(f.id ? `/api/conectores/${f.id}` : '/api/conectores', {
        nombre: f.nombre, titulo: f.titulo, descripcion: f.descripcion || null, tipo: f.tipo, url: f.url, autenticacion: f.autenticacion,
        campo: f.campo || null, secretoId, operaciones, ocultas: f.ocultas, estado: f.estado,
      });
    },
    onSuccess: (r) => {
      recargar();
      setForm(null);
      aviso(r.error ? 'Guardado, pero no respondió: revisar la dirección y la credencial' : `Conectado: ${r.herramientas} herramientas`);
      setAbierto(r.id);
    },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const descubrir = useMutation({
    mutationFn: (id: string) => api<{ herramientas: any[]; error: string | null }>(`/api/conectores/${id}/descubrir`, {}),
    onSuccess: (r) => { recargar(); aviso(r.error ? 'No respondió' : `${r.herramientas.length} herramientas`); },
  });
  const borrar = useMutation({
    mutationFn: (id: string) => api(`/api/conectores/${id}/borrar`, {}),
    onSuccess: () => { recargar(); aviso('Conector quitado'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const alternarOculta = useMutation({
    mutationFn: async ({ c, herramienta }: { c: any; herramienta: string }) => {
      const ocultas: string[] = c.ocultas.includes(herramienta) ? c.ocultas.filter((x: string) => x !== herramienta) : [...c.ocultas, herramienta];
      return api(`/api/conectores/${c.id}`, {
        nombre: c.nombre, titulo: c.titulo, descripcion: c.descripcion, tipo: c.tipo, url: c.url, autenticacion: c.autenticacion, campo: c.campo,
        secretoId: c.secreto_id, operaciones: c.operaciones, ocultas, estado: c.estado,
      });
    },
    onSuccess: () => recargar(),
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const probar = useMutation({
    mutationFn: async (p: NonNullable<typeof prueba>) => {
      let argumentos: Record<string, unknown> = {};
      if (p.args.trim()) argumentos = JSON.parse(p.args);
      return api<{ content: { type: string; text?: string }[]; isError?: boolean }>(`/api/conectores/${p.conector.id}/probar`, { herramienta: p.herramienta.nombre, argumentos });
    },
    onSuccess: (r) => setPrueba((p) => p && { ...p, resultado: r.content.map((x) => x.text ?? `[${x.type}]`).join('\n'), error: Boolean(r.isError) }),
    onError: (e) => setPrueba((p) => p && { ...p, resultado: mensajeDeError(e), error: true }),
  });

  if (q.isLoading) return <Cargando />;
  const plantillas = conectoresPlantilla(config.data?.mcpGlubi ?? '');
  const cambiarOp = (i: number, cambio: Partial<Operacion>) => setForm((f) => f && { ...f, operaciones: f.operaciones.map((o, j) => (j === i ? { ...o, ...cambio } : o)) });

  return (
    <div className="page">
      <PageHead title="Conectores" subtitle="El POS, la contabilidad, la tienda en línea, los sistemas internos y el propio Glubi, conectados una vez. Los agentes los usan por un solo MCP, con la llave que les dé el negocio.">
        <SoloSiAdministra><Button icon="plus" onClick={() => { setError(null); setElegir(true); }}>Conectar</Button></SoloSiAdministra>
      </PageHead>
      <AvisoSoloVer />
      {config.data && <div style={{ marginBottom: 18 }}><Copiable etiqueta="El MCP del negocio (con una llave del gateway)" texto={config.data.mcp} /></div>}

      {!q.data?.length ? (
        <Vacio titulo="Nada conectado todavía" accion={<SoloSiAdministra><Button icon="plus" onClick={() => setElegir(true)}>Conectar el primero</Button></SoloSiAdministra>}>
          Empezar por el MCP de Glubi o por el sistema del que más se copia a mano.
        </Vacio>
      ) : (
        <div className="stack">
          {q.data.map((c) => {
            const herramientas: any[] = c.herramientas ?? [];
            const visibles = herramientas.filter((h) => !c.ocultas.includes(h.nombre)).length;
            return (
              <div key={c.id} className="card">
                <div className="row-line" style={{ alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="fila">
                      <span className="h-card">{c.titulo}</span>
                      <Tag>{c.tipo === 'mcp' ? 'MCP' : 'API REST'}</Tag>
                      {c.estado !== 'activo' ? <Estado estado={c.estado} /> : c.ultimo_error ? <Badge state="error">No responde</Badge> : c.descubiertas_en ? <Badge state="ok">Conectado</Badge> : <Badge state="pending">Sin revisar</Badge>}
                    </div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                      <code>{c.nombre}__…</code> · {c.url} · {c.secreto ? `credencial «${c.secreto}»` : 'sin credencial'} · {visibles} de {herramientas.length} herramientas a la vista · {numero(c.llamadas_7d)} llamadas en 7 días
                    </div>
                    {c.descripcion && <div style={{ fontSize: 13, marginTop: 6 }}>{c.descripcion}</div>}
                    {c.ultimo_error && c.estado === 'activo' && <div className="error-caja" style={{ marginTop: 8 }}>{c.ultimo_error}</div>}
                  </div>
                  <div className="fila" style={{ justifyContent: 'flex-end' }}>
                    <Button size="sm" variant="ghost" icon={abierto === c.id ? 'chevron-down' : 'arrow-right'} onClick={() => setAbierto(abierto === c.id ? null : c.id)}>Herramientas</Button>
                    <SoloSiAdministra>
                      <Button size="sm" variant="ghost" icon="refresh" disabled={descubrir.isPending} onClick={() => descubrir.mutate(c.id)}>Revisar</Button>
                      <Button size="sm" variant="secondary" icon="pencil" onClick={() => {
                        setError(null);
                        setForm({
                          id: c.id, nombre: c.nombre, titulo: c.titulo, descripcion: c.descripcion ?? '', tipo: c.tipo, url: c.url, autenticacion: c.autenticacion, campo: c.campo ?? '',
                          credencial: c.autenticacion === 'ninguna' ? { modo: 'ninguna' } : credencialInicial(c.secreto_id), ocultas: c.ocultas, estado: c.estado,
                          operaciones: (c.operaciones ?? []).map((o: OperacionPlantilla) => ({ ...o, parametros: aTexto(o.parametros) })),
                        });
                      }}>Cambiar</Button>
                      <Button size="sm" variant="ghost" icon="trash" onClick={() => { if (confirm(`¿Desconectar «${c.titulo}»? Sus herramientas dejan de aparecer para todos los agentes.`)) borrar.mutate(c.id); }}>Quitar</Button>
                    </SoloSiAdministra>
                  </div>
                </div>
                {abierto === c.id && (
                  <div style={{ borderTop: '1px solid var(--border-default)' }}>
                    {!herramientas.length && <div className="row-line muted" style={{ fontSize: 14 }}>Sin herramientas descubiertas. «Revisar» vuelve a preguntar.</div>}
                    {herramientas.map((h) => {
                      const oculta = c.ocultas.includes(h.nombre);
                      return (
                        <div key={h.nombre} className="row-line" style={{ opacity: oculta ? 0.55 : 1 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="fila"><code style={{ fontSize: 13 }}>{c.nombre}__{h.nombre}</code>{h.lectura ? <Tag>solo lee</Tag> : <Tag tone="amber">puede escribir</Tag>}{oculta && <Tag>oculta</Tag>}</div>
                            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{h.descripcion}</div>
                          </div>
                          <SoloSiAdministra>
                            <Button size="sm" variant="ghost" icon="play" disabled={oculta} onClick={() => setPrueba({ conector: c, herramienta: h, args: ejemploArgs(h.esquema) })}>Probar</Button>
                            <Button size="sm" variant="ghost" icon={oculta ? 'eye' : 'x'} onClick={() => alternarOculta.mutate({ c, herramienta: h.nombre })}>{oculta ? 'Mostrar' : 'Ocultar'}</Button>
                          </SoloSiAdministra>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {elegir && (
        <Modal title="Conectar un sistema" subtitle="Elegir un punto de partida. Todo se puede cambiar antes de guardar." onClose={() => setElegir(false)} width={760}>
          <div className="plantillas">
            {plantillas.map((p) => (
              <button key={p.clave} className="plantilla" onClick={() => { setElegir(false); setForm(desdePlantilla(p)); }}>
                <b>{p.titulo}</b><span>{p.descripcion}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {form && (
        <Modal title={form.id ? `Cambiar «${form.titulo}»` : 'Conectar'} onClose={() => setForm(null)} width={800}
          subtitle={form.tipo === 'mcp' ? 'Servidor MCP: sus herramientas se descubren al guardar.' : 'API REST: cada operación se vuelve una herramienta. Los parámetros de la ruta van entre llaves: /productos/{id}.'}
          footer={<>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
            <Button disabled={guardar.isPending || !form.titulo.trim() || !/^[a-z][a-z0-9-]{1,30}$/.test(form.nombre) || !form.url.startsWith('http')} onClick={() => { setError(null); guardar.mutate(form); }}>
              {guardar.isPending ? 'Conectando…' : 'Guardar y probar'}
            </Button>
          </>}>
          <div className="grid-2" style={{ gap: 12 }}>
            <Input label="Nombre visible" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value, ...(form.id || form.nombre ? {} : { nombre: slug(e.target.value) }) })} placeholder="POS del local" />
            <Input label="Prefijo de sus herramientas" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toLowerCase() })}
              helper="minúsculas, números y guiones: pos, siigo, inventario-bodega" error={form.nombre && !/^[a-z][a-z0-9-]{1,30}$/.test(form.nombre) ? 'Empieza con letra; 2 a 31 caracteres' : undefined} />
          </div>
          <Input label="Para qué sirve (lo leen los agentes)" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          <div className="grid-2" style={{ gap: 12 }}>
            <Select label="Tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as 'mcp' | 'http' })} options={[{ value: 'mcp', label: 'Servidor MCP' }, { value: 'http', label: 'API REST' }]} />
            <Input label={form.tipo === 'mcp' ? 'Dirección del servidor MCP' : 'Dirección base de la API'} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
          </div>
          <div className="grid-2" style={{ gap: 12 }}>
            <Select label="Autenticación" value={form.autenticacion} options={AUTH} onChange={(e) => {
              const autenticacion = e.target.value as Auth;
              setForm({ ...form, autenticacion, credencial: autenticacion === 'ninguna' ? { modo: 'ninguna' } : form.credencial.modo === 'ninguna' ? { modo: 'nueva', nombre: `Credencial de ${form.titulo}`, valor: '' } : form.credencial });
            }} />
            {(form.autenticacion === 'encabezado' || form.autenticacion === 'consulta') && (
              <Input label={form.autenticacion === 'encabezado' ? 'Nombre del encabezado' : 'Nombre del parámetro'} value={form.campo} onChange={(e) => setForm({ ...form, campo: e.target.value })}
                placeholder={form.autenticacion === 'encabezado' ? 'x-api-key' : 'api_key'} />
            )}
          </div>
          {form.autenticacion !== 'ninguna' && <ElegirCredencial valor={form.credencial} onChange={(credencial) => setForm({ ...form, credencial })} nombreSugerido={`Credencial de ${form.titulo}`} ayuda={form.ayuda} />}

          {form.tipo === 'http' && <>
            <span className="etiqueta">Operaciones</span>
            {form.operaciones.map((o, i) => (
              <div key={i} className="operacion">
                <div className="grid-2" style={{ gap: 10 }}>
                  <Input label="Nombre de la herramienta" value={o.nombre} onChange={(e) => cambiarOp(i, { nombre: e.target.value })} placeholder="ventas_del_dia" />
                  <div className="fila" style={{ alignItems: 'flex-end', flexWrap: 'nowrap' }}>
                    <div style={{ width: 120 }}><Select label="Método" value={o.metodo} options={['GET', 'POST', 'PUT', 'PATCH', 'DELETE']} onChange={(e) => cambiarOp(i, { metodo: e.target.value as Operacion['metodo'] })} /></div>
                    <div style={{ flex: 1 }}><Input label="Ruta" value={o.ruta} onChange={(e) => cambiarOp(i, { ruta: e.target.value })} placeholder="/ventas/{fecha}" /></div>
                  </div>
                </div>
                <Input label="Qué hace (lo leen los agentes)" value={o.descripcion} onChange={(e) => cambiarOp(i, { descripcion: e.target.value })} />
                <Area label="Parámetros (JSON Schema, opcional)" mono filas={3} value={o.parametros} onChange={(v) => cambiarOp(i, { parametros: v })}
                  placeholder={'{ "properties": { "fecha": { "type": "string" } }, "required": ["fecha"] }'} />
                <div><Button size="sm" variant="ghost" icon="trash" onClick={() => setForm({ ...form, operaciones: form.operaciones.filter((_, j) => j !== i) })}>Quitar operación</Button></div>
              </div>
            ))}
            <div><Button size="sm" variant="secondary" icon="plus" onClick={() => setForm({ ...form, operaciones: [...form.operaciones, { nombre: '', descripcion: '', metodo: 'GET', ruta: '/', parametros: '' }] })}>Agregar operación</Button></div>
            <div className="muted" style={{ fontSize: 12 }}>En GET y DELETE los argumentos que no van en la ruta viajan como parámetros de la URL; en los demás métodos, como cuerpo JSON.</div>
          </>}
          {form.id && <Select label="Estado" value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value as 'activo' | 'pausado' })} options={[{ value: 'activo', label: 'Activo' }, { value: 'pausado', label: 'Pausado: sus herramientas no aparecen' }]} />}
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}

      {prueba && (
        <Modal title={`Probar ${prueba.conector.nombre}__${prueba.herramienta.nombre}`} subtitle={prueba.herramienta.lectura ? 'Solo lee.' : 'Ojo: esta herramienta puede escribir en el sistema conectado, y la prueba es de verdad.'}
          onClose={() => setPrueba(null)} width={680}
          footer={<>
            <Button variant="ghost" onClick={() => setPrueba(null)}>Cerrar</Button>
            <Button icon="play" disabled={probar.isPending} onClick={() => probar.mutate(prueba)}>{probar.isPending ? 'Llamando…' : 'Llamar'}</Button>
          </>}>
          <Area label="Argumentos (JSON)" mono filas={6} value={prueba.args} onChange={(args) => setPrueba({ ...prueba, args })} />
          {prueba.resultado !== undefined && <pre className="codigo" style={{ maxHeight: 300, overflow: 'auto', borderColor: prueba.error ? 'var(--state-error)' : undefined }}>{prueba.resultado}</pre>}
          <div className="muted" style={{ fontSize: 12 }}>Queda en el registro de llamadas, como la de cualquier agente. Última revisión de herramientas: {desde(prueba.conector.descubiertas_en)}.</div>
        </Modal>
      )}
    </div>
  );
}

// El ejemplo que trae la herramienta, si trae; si no, un esqueleto con la forma del esquema, para no empezar de una caja vacía.
function ejemploArgs(esquema: any): string {
  return JSON.stringify(esquema?.examples?.[0] ?? esqueleto(esquema) ?? {}, null, 2);
}

function esqueleto(s: any): unknown {
  if (!s || typeof s !== 'object') return '';
  if (s.examples?.length) return s.examples[0];
  if (s.default !== undefined) return s.default;
  if (s.enum?.length) return s.enum[0];
  const variante = s.anyOf ?? s.oneOf;
  if (variante?.length) return esqueleto(variante.find((v: any) => v?.type !== 'null') ?? variante[0]);
  const tipo = Array.isArray(s.type) ? s.type.find((t: string) => t !== 'null') : s.type;
  if (tipo === 'object' || s.properties) {
    // Lo opcional queda fuera, salvo que no haya nada obligatorio: así se ve qué pide la herramienta.
    const props = Object.entries(s.properties ?? {});
    const requeridos = new Set<string>(s.required ?? []);
    const elegidas = requeridos.size ? props.filter(([k]) => requeridos.has(k)) : props;
    return Object.fromEntries(elegidas.map(([k, v]) => [k, esqueleto(v)]));
  }
  if (tipo === 'array') return [esqueleto(s.items)];
  if (tipo === 'integer' || tipo === 'number') return s.minimum ?? 0;
  if (tipo === 'boolean') return false;
  return '';
}
